import { useCallback, useRef } from 'react';

import { useLatest } from './useLatest';

// Find more at https://github.com/ai/easings.net
const EASINGS = {
	easeInQuad: (x: number) => x * x,
	easeOutQuad: (x: number) => 1 - (1 - x) * (1 - x),
};

/** Get a function that animates a value from one number to another. */
export function useAnimator({
	duration,
	easing = 'easeOutQuad',
	onDone: onDoneProp,
	onStart: onStartProp,
	onUpdate: onUpdateProp,
}: {
	duration: number;
	easing?: keyof typeof EASINGS;
	onDone?: (toValue: number) => void;
	onStart?: (toValue: number) => void;
	onUpdate: (intermediateValue: number) => void;
}) {
	const animationStartTimeRef = useRef<number>(null);
	const ease = EASINGS[easing];
	const onDone = useLatest(onDoneProp);
	const onStart = useLatest(onStartProp);
	const onUpdate = useLatest(onUpdateProp);

	return useCallback(
		(from: number, to: number) => {
			const startTime = Date.now();
			animationStartTimeRef.current = startTime;
			onStart.current?.(to);

			function update() {
				const elapsed = Date.now() - startTime;
				const progress = Math.min(elapsed / duration, 1);

				onUpdate.current?.(from + (to - from) * ease(progress));

				// A new animation has been started, abort current.
				if (animationStartTimeRef.current !== startTime) {
					return;
				}

				if (progress < 1) {
					requestAnimationFrame(update);
				} else {
					animationStartTimeRef.current = null;
					onDone.current?.(to);
				}
			}
			update();
		},
		[duration, ease, onDone, onStart, onUpdate],
	);
}
